import React, { useEffect, useState } from 'react';
import { useAuth } from '../AuthProvider';
import AuthShell, { Label, Input, PrimaryButton, ErrorBanner } from '../AuthShell';

export default function MfaEnrollPage() {
  const { startMfaEnrollment, completeMfaEnrollment } = useAuth();
  const [enroll, setEnroll] = useState(null);   // { qr_code, provisioning_uri, recovery_codes }
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [loadingStart, setLoadingStart] = useState(true);
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [recoveryAck, setRecoveryAck] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await startMfaEnrollment();
        if (!cancelled) setEnroll(data);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Could not start MFA enrollment.');
      } finally {
        if (!cancelled) setLoadingStart(false);
      }
    })();
    return () => { cancelled = true; };
  }, [startMfaEnrollment]);

  const onVerify = async (e) => {
    e.preventDefault();
    setError(null);
    if (!recoveryAck) { setError('Please confirm you saved the recovery codes.'); return; }
    setLoadingVerify(true);
    try {
      await completeMfaEnrollment(code.trim());
    } catch (err) {
      setError(
        err?.detail === 'invalid_mfa_code' ? 'That code didn\'t match. Try again.' :
        err?.message || 'Could not verify code.'
      );
    } finally {
      setLoadingVerify(false);
    }
  };

  return (
    <AuthShell
      title="Set up two-factor authentication"
      subtitle="Scan the QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.)"
    >
      {loadingStart ? (
        <p className="text-sm text-surface-muted dark:text-surface-dark-muted">Loading…</p>
      ) : !enroll ? (
        <ErrorBanner>{error || 'Could not load MFA setup.'}</ErrorBanner>
      ) : (
        <>
          <div className="flex flex-col items-center mb-4">
            {enroll.qrcode_data_url ? (
              <img
                src={enroll.qrcode_data_url}
                alt="MFA QR code"
                className="w-48 h-48 rounded-thin bg-white p-2 border border-surface-border"
              />
            ) : (
              <code className="text-xs break-all p-2 bg-surface-subtle dark:bg-surface-dark-subtle rounded-thin">
                {enroll.otpauth_uri}
              </code>
            )}
            <details className="mt-2 text-xs text-surface-muted dark:text-surface-dark-muted">
              <summary className="cursor-pointer hover:text-continuum-500">Can't scan? Show manual code</summary>
              <div className="mt-2 p-2 bg-surface-subtle dark:bg-surface-dark-subtle rounded-thin font-mono break-all">
                {enroll.secret}
              </div>
            </details>
          </div>

          {enroll.recovery_codes?.length > 0 && (
            <div className="mb-4">
              <Label>Recovery codes</Label>
              <div className="grid grid-cols-2 gap-1 p-3 rounded-thin bg-surface-subtle dark:bg-surface-dark-subtle font-mono text-xs">
                {enroll.recovery_codes.map((c) => <div key={c}>{c}</div>)}
              </div>
              <label className="mt-2 flex items-start text-xs text-surface-text dark:text-surface-dark-text">
                <input
                  type="checkbox"
                  checked={recoveryAck}
                  onChange={(e) => setRecoveryAck(e.target.checked)}
                  className="mt-0.5 mr-2"
                />
                I've saved these recovery codes somewhere safe. Each works once if I lose my authenticator.
              </label>
            </div>
          )}

          <form onSubmit={onVerify} className="space-y-4">
            <ErrorBanner>{error}</ErrorBanner>
            <div>
              <Label htmlFor="code">Enter the 6-digit code from your authenticator</Label>
              <Input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                maxLength={6}
                pattern="[0-9]{6}"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="tracking-widest text-center font-mono text-lg"
              />
            </div>
            <PrimaryButton type="submit" loading={loadingVerify}>Verify and enable MFA</PrimaryButton>
          </form>
        </>
      )}
    </AuthShell>
  );
}
